import type { GorevRengi } from "@koclukpanel/contracts";
import type { UnkDurum } from "@koclukpanel/ui";

/**
 * PROGRAM GÖRÜNÜMÜNÜN SAF YARDIMCILARI — sürüm 9a (UNİKOÇ birebir)
 *
 * Renk SUNUCUDA hesaplanıyor (`gorevRengi`, F-STV-02a); burası yalnız o rengi
 * eski panelin dört durum diline ÇEVİRİYOR. Yeni bir kural yok: hangi görevin
 * "tamamlandı" sayılacağı burada değil, sunucuda karar veriliyor.
 */

export interface DurumGorevi {
  itemDate: string;
  renk?: GorevRengi;
  isExcused: boolean;
  status: string;
}

export interface GorunenDurum {
  /** Rozet/çizgi rengi; `null` = sunucu renk göndermedi, uydurulmuyor. */
  durum: UnkDurum | null;
  etiket: string;
}

/**
 * Sunucu rengi → UNİKOÇ dörtlüsü.
 *
 *   YESIL   → tamamlandı (yeşil)
 *   KIRMIZI → "Rapor girilmedi" (kırmızı) — eski panelin kendi cümlesi
 *   SARI    → turuncu, "Hedefin altında": UNİKOÇ'ta sarı yok; veri girilmiş ama
 *             hedef tutmamış görev "eksik" DEĞİL (öğrenci çalışmış), "planlı" hiç
 *             değil. Turuncu, eski panelde de "dikkat" rengi.
 *   NOTR    → bugünse "Bekliyor (Bugün)" turuncu, değilse mavi "Planlanmış".
 *             Mazeretli ve ertelenen görev de NOTR geliyor; ikisi kendi adıyla
 *             yazılıyor ki koç "planlı" sanıp öğrenciye sormasın.
 */
export function gorunenDurum(g: DurumGorevi, bugun: string): GorunenDurum {
  switch (g.renk) {
    case "YESIL":
      return { durum: "tamamlandi", etiket: "Tamamlandı" };
    case "KIRMIZI":
      return { durum: "eksik", etiket: "Rapor girilmedi" };
    case "SARI":
      return { durum: "bekliyor", etiket: "Hedefin altında" };
    case "NOTR":
      if (g.isExcused) return { durum: "planli", etiket: "Mazeretli" };
      if (g.status === "POSTPONED") return { durum: "planli", etiket: "Ertelendi" };
      return g.itemDate.slice(0, 10) === bugun
        ? { durum: "bekliyor", etiket: "Bekliyor (Bugün)" }
        : { durum: "planli", etiket: "Planlanmış" };
    default:
      return { durum: null, etiket: "Durum alınamadı" };
  }
}

/** Öğrenci gözü süzgeçleri — eski paneldeki sırayla. */
export const OGRENCI_SUZGECLERI = [
  "Tümü",
  "Bugün",
  "Tamamlanan",
  "Tamamlanmayan",
  "Planlanmış",
] as const;
export type OgrenciSuzgeci = (typeof OGRENCI_SUZGECLERI)[number];

/**
 * Süzgeç eşleşmesi. "Tamamlanmayan" SARI + KIRMIZI: ikisi de hedefe ulaşmamış
 * görev. "Planlanmış" yalnız gerçekten bekleyen NOTR — mazeretli/ertelenen
 * görevi "planlı" listesine koymak, yapılmayacak bir işi bekletmek olurdu.
 */
export function suzgeceUyar(g: DurumGorevi, suzgec: OgrenciSuzgeci, bugun: string): boolean {
  switch (suzgec) {
    case "Tümü":
      return true;
    case "Bugün":
      return g.itemDate.slice(0, 10) === bugun;
    case "Tamamlanan":
      return g.renk === "YESIL";
    case "Tamamlanmayan":
      return g.renk === "SARI" || g.renk === "KIRMIZI";
    case "Planlanmış":
      return g.renk === "NOTR" && !g.isExcused && g.status !== "POSTPONED";
  }
}

/** `2026-09-07` → `07.09.2026` — eski panelin tarih biçimi. Yalnız metin; saat dilimi yok. */
export function noktaliTarih(isoGun: string): string {
  const [yil, ay, gun] = isoGun.slice(0, 10).split("-");
  return yil && ay && gun ? `${gun}.${ay}.${yil}` : isoGun;
}

/**
 * Ders adından daire kısaltması: "TYT Matematik" → "MAT", "AYT Fizik" → "FİZ".
 *
 * Sınav öneki ATILIYOR: eski panelde ders adları öneksizdi ("MATEMATİK" → MAT);
 * önekten kısaltma çıkarsaydık her TYT dersi "TYT" olurdu ve daire hiçbir şey
 * söylemezdi.
 */
export function dersKisaltmasi(ad: string | undefined): string {
  const kelimeler = (ad ?? "").trim().split(/\s+/).filter(Boolean);
  const govde =
    kelimeler.length > 1 && /^(TYT|AYT|YDT|LGS)$/i.test(kelimeler[0]!)
      ? kelimeler.slice(1)
      : kelimeler;
  const ilk = govde[0] ?? "";
  return ilk.slice(0, 3).toLocaleUpperCase("tr-TR") || "—";
}
